class AirackerWishlist {
  constructor() {
    this.key = 'airacker:wishlist:v1';
    this.drawer = document.querySelector('[data-airacker-wishlist-drawer]');
    this.overlay = document.querySelector('[data-airacker-wishlist-overlay]');
    this.lists = [...document.querySelectorAll('[data-airacker-wishlist-list]')];
    this.items = this.read();
    this.lastOpener = null;
    this.bind();
    this.render();
  }

  read() {
    try {
      return JSON.parse(localStorage.getItem(this.key)) || [];
    } catch {
      return [];
    }
  }

  save() {
    try {
      localStorage.setItem(this.key, JSON.stringify(this.items));
    } catch (error) {
      console.warn('AIRACKER wishlist could not be saved in this browser.', error);
    }
    document.dispatchEvent(new CustomEvent('airacker:wishlist-change', { detail: this.items }));
  }

  bind() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-airacker-wishlist-toggle]');
      const opener = event.target.closest('[data-airacker-wishlist-open]');
      const closer = event.target.closest('[data-airacker-wishlist-close]');
      const remover = event.target.closest('[data-airacker-wishlist-remove]');

      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle(toggle);
        return;
      }
      if (opener) {
        event.preventDefault();
        this.open();
      }
      if (closer) this.close();
      if (remover) this.remove(remover.dataset.airackerWishlistRemove);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
    });
    document.addEventListener('airacker:product-grid-updated', () => this.render());
  }

  toggle(button) {
    const id = button.dataset.productId;
    if (!id) return;
    const exists = this.items.some((item) => item.id === id);
    if (exists) {
      this.items = this.items.filter((item) => item.id !== id);
    } else {
      this.items.push({
        id,
        title: button.dataset.productTitle,
        url: button.dataset.productUrl,
        image: button.dataset.productImage,
        price: button.dataset.productPrice,
      });
    }
    this.save();
    this.render();
    button.setAttribute('aria-pressed', String(!exists));
    if (!exists) this.open();
  }

  remove(id) {
    this.items = this.items.filter((item) => item.id !== id);
    this.save();
    this.render();
  }

  render() {
    document.querySelectorAll('[data-airacker-wishlist-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', String(this.items.some((item) => item.id === button.dataset.productId)));
    });
    document.querySelectorAll('[data-airacker-wishlist-count]').forEach((node) => {
      node.textContent = this.items.length;
      if (node.parentElement?.classList.contains('cart-count-bubble')) {
        node.parentElement.hidden = this.items.length === 0;
      }
    });
    this.lists.forEach((list) => {
      if (!this.items.length) {
        list.innerHTML = `<p>${this.escape(list.dataset.emptyText)}</p>`;
        return;
      }
      list.innerHTML = this.items.map((item) => `
        <article class="airacker-wishlist-item">
          <a class="airacker-wishlist-item__image" href="${this.escape(item.url)}">
            <img src="${this.escape(item.image)}" alt="" width="132" height="132" loading="lazy">
          </a>
          <div class="airacker-wishlist-item__content">
            <a class="airacker-wishlist-item__title" href="${this.escape(item.url)}">${this.escape(item.title)}</a>
            <p>${this.escape(item.price)}</p>
            <button
              class="airacker-wishlist-item__add"
              type="button"
              data-airacker-wishlist-add="${this.escape(item.url)}"
              data-adding-text="${this.escape(list.dataset.addingText)}"
            >${this.escape(list.dataset.addToCartText)}</button>
          </div>
          <details class="airacker-wishlist-item__edit">
            <summary class="airacker-icon-button" aria-label="${this.escape(list.dataset.editText)}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="5" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="12" cy="19" r="1.7"/></svg>
            </summary>
            <div class="airacker-wishlist-item__menu">
              <a href="${this.escape(item.url)}">${this.escape(list.dataset.viewProductText)}</a>
              <button type="button" data-airacker-wishlist-remove="${this.escape(item.id)}">${this.escape(list.dataset.removeText)}</button>
            </div>
          </details>
        </article>
      `).join('');
    });
  }

  escape(value = '') {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }

  open() {
    if (!this.drawer) return;
    this.lastOpener = document.activeElement;
    this.drawer.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('airacker-lock');
    this.drawer.querySelector('button')?.focus();
  }

  close() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.drawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('airacker-lock');
    if (this.lastOpener instanceof HTMLElement) this.lastOpener.focus();
  }
}

class AirackerQuickAdd {
  constructor() {
    this.dialog = document.querySelector('[data-airacker-quick-add-dialog]');
    this.content = this.dialog?.querySelector('[data-airacker-quick-add-content]');
    if (!this.dialog || !this.content) return;
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-airacker-quick-add]');
      const wishlistAdd = event.target.closest('[data-airacker-wishlist-add]');
      const closer = event.target.closest('[data-airacker-quick-add-close]');
      if (opener) this.open(opener.dataset.airackerQuickAdd);
      if (wishlistAdd) this.addFromWishlist(wishlistAdd.dataset.airackerWishlistAdd, wishlistAdd);
      if (closer) this.dialog.close();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  async open(productUrl) {
    this.content.innerHTML = '<span class="loading__spinner"></span>';
    this.dialog.showModal();
    try {
      const url = new URL(productUrl, window.location.origin);
      const response = await fetch(`${url.pathname}.js`);
      if (!response.ok) throw new Error('Product unavailable');
      this.product = await response.json();
      this.render();
    } catch (error) {
      this.content.textContent = error.message;
    }
  }

  async addFromWishlist(productUrl, button) {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = button.dataset.addingText || 'Adding…';
    try {
      const url = new URL(productUrl, window.location.origin);
      const response = await fetch(`${url.pathname}.js`);
      if (!response.ok) throw new Error('Product unavailable');
      const product = await response.json();
      const availableVariants = product.variants.filter((variant) => variant.available);
      if (availableVariants.length === 1) {
        await this.addVariant(availableVariants[0].id);
      } else {
        button.disabled = false;
        button.textContent = originalText;
        this.product = product;
        this.dialog.showModal();
        this.render();
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = error.message;
      window.setTimeout(() => { button.textContent = originalText; }, 2200);
    }
  }

  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }

  render() {
    const variant = this.product.variants.find((item) => item.available) || this.product.variants[0];
    const selectors = this.product.options.map((option, index) => {
      const name = typeof option === 'string' ? option : option.name;
      const values = Array.isArray(option.values) ? option.values : [...new Set(this.product.variants.map((item) => item.options[index]))];
      return `<label>${this.escape(name)}<select data-airacker-option="${index}">${values.map((value) => `<option${variant.options[index] === value ? ' selected' : ''}>${this.escape(value)}</option>`).join('')}</select></label>`;
    }).join('');
    this.content.innerHTML = `
      <div class="airacker-quick-add__product">
        <img src="${this.escape(this.product.featured_image || '')}" alt="" width="360" height="360">
        <div><h2>${this.escape(this.product.title)}</h2><p data-airacker-quick-add-price>${this.money(variant.price)}</p></div>
      </div>
      <form data-airacker-quick-add-form>
        <div class="airacker-quick-add__options">${selectors}</div>
        <button class="airacker-button" type="submit"${variant.available ? '' : ' disabled'}>${this.escape(window.variantStrings?.addToCart || 'Add to cart')}</button>
      </form>`;
    this.selectedVariant = variant;
    this.content.querySelectorAll('select').forEach((select) => select.addEventListener('change', () => this.updateVariant()));
    this.content.querySelector('form').addEventListener('submit', (event) => this.add(event));
  }

  updateVariant() {
    const selected = [...this.content.querySelectorAll('select')].map((select) => select.value);
    this.selectedVariant = this.product.variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
    const button = this.content.querySelector('button[type="submit"]');
    button.disabled = !this.selectedVariant?.available;
    button.textContent = this.selectedVariant?.available ? (window.variantStrings?.addToCart || 'Add to cart') : (window.variantStrings?.soldOut || 'Sold out');
    this.content.querySelector('[data-airacker-quick-add-price]').textContent = this.money(this.selectedVariant?.price || 0);
  }

  async add(event) {
    event.preventDefault();
    const button = event.currentTarget.querySelector('button[type="submit"]');
    button.disabled = true;
    try {
      await this.addVariant(this.selectedVariant.id, true);
    } catch (error) {
      button.disabled = false;
      button.textContent = error.message;
    }
  }

  async addVariant(variantId, closeDialog = false) {
    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
    });
    const state = await response.json();
    if (!response.ok) throw new Error(state.description || window.cartStrings.error);
    if (closeDialog) this.dialog.close();
    document.querySelector('[data-airacker-wishlist-drawer]')?.classList.remove('is-open');
    document.querySelector('[data-airacker-wishlist-overlay]')?.classList.remove('is-open');
    document.querySelector('[data-airacker-wishlist-drawer]')?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('airacker-lock');
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.renderContents) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(state);
    } else window.location.assign(window.routes.cart_url);
  }

  escape(value = '') {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }
}

class AirackerPopup {
  constructor(node) {
    this.node = node;
    this.panel = node.querySelector('[role="dialog"]');
    this.key = 'airacker:newsletter-dismissed';
    this.successKey = 'airacker:newsletter-subscribed';
    this.bind();
    this.schedule();
  }

  schedule() {
    const template = this.node.dataset.template || '';
    if (/customers|password/i.test(template)) return;
    if (this.node.dataset.subscribed === 'true') {
      localStorage.setItem(this.successKey, 'true');
      this.open();
      return;
    }
    if (localStorage.getItem(this.successKey) === 'true') return;
    const dismissed = Number(localStorage.getItem(this.key) || 0);
    const days = Number(this.node.dataset.suppressDays || 7) * 86400000;
    if (Date.now() - dismissed < days) return;
    window.setTimeout(() => this.open(), Number(this.node.dataset.delay || 3000));
  }

  bind() {
    this.node.querySelectorAll('[data-airacker-popup-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(true));
    });
    document.addEventListener('keydown', (event) => {
      if (!this.node.hidden && event.key === 'Escape') this.close(true);
      if (!this.node.hidden && event.key === 'Tab') this.trap(event);
    });
  }

  open() {
    this.node.hidden = false;
    document.body.classList.add('airacker-lock');
    this.panel?.querySelector('input, button')?.focus();
  }

  close(remember) {
    this.node.hidden = true;
    document.body.classList.remove('airacker-lock');
    if (remember) localStorage.setItem(this.key, String(Date.now()));
  }

  trap(event) {
    const focusable = [...this.panel.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

const bindAirackerMegaMenus = (scope = document) => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  scope.querySelectorAll('[data-airacker-mega-menu]').forEach((menu) => {
    if (menu.dataset.airackerMegaBound === 'true') return;
    menu.dataset.airackerMegaBound = 'true';

    let closeTimer;
    const cancelClose = () => window.clearTimeout(closeTimer);
    const closeWithDelay = () => {
      cancelClose();
      closeTimer = window.setTimeout(() => menu.removeAttribute('open'), 220);
    };

    menu.addEventListener('mouseenter', () => {
      if (!desktopQuery.matches) return;
      cancelClose();
      document.querySelectorAll('[data-airacker-mega-menu][open]').forEach((openMenu) => {
        if (openMenu !== menu) openMenu.removeAttribute('open');
      });
      menu.setAttribute('open', '');
    });

    menu.addEventListener('mouseleave', () => {
      if (desktopQuery.matches) closeWithDelay();
    });

    menu.addEventListener('focusout', (event) => {
      if (desktopQuery.matches && !menu.contains(event.relatedTarget)) closeWithDelay();
    });

    menu.addEventListener('focusin', cancelClose);
  });
};

const bindAirackerRailControls = (scope = document) => {
  scope.querySelectorAll('[data-airacker-rail-control]').forEach((button) => {
    if (button.dataset.airackerRailBound === 'true') return;
    button.dataset.airackerRailBound = 'true';
    button.addEventListener('click', () => {
      const rail = document.getElementById(button.dataset.airackerRailControl);
      if (rail?.matches('[data-airacker-video-rail]')) {
        const realCards = [...rail.querySelectorAll(':scope > [data-airacker-video-card]:not([data-airacker-video-clone])')];
        const activeCard = rail.querySelector(':scope > [data-airacker-video-card][aria-current="true"]');
        const activeIndex = Math.max(0, realCards.indexOf(activeCard));
        const direction = button.dataset.direction === 'next' ? 1 : -1;
        const targetIndex = (activeIndex + direction + realCards.length) % realCards.length;
        const targetCard = realCards[targetIndex];
        if (targetCard) {
          rail.scrollTo({
            left: targetCard.offsetLeft - (rail.clientWidth - targetCard.offsetWidth) / 2,
            behavior: 'smooth',
          });
        }
        return;
      }
      const card = rail?.querySelector(':scope > *');
      if (!rail || !card) return;
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
      const direction = button.dataset.direction === 'next' ? 1 : -1;
      rail.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    });
  });
};

const bindAirackerDraggableRails = (scope = document) => {
  scope.querySelectorAll('[data-airacker-draggable-rail]').forEach((rail) => {
    if (rail.dataset.airackerDragBound === 'true') return;
    rail.dataset.airackerDragBound = 'true';
    let activePointerId = null;
    let startX = 0;
    let startScroll = 0;
    let dragged = false;
    let suppressClick = false;
    let suppressClickUntil = 0;

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (activePointerId !== null) return;
      activePointerId = event.pointerId;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      dragged = false;
      rail.classList.add('is-pressing');
    });
    rail.addEventListener('pointermove', (event) => {
      if (activePointerId !== event.pointerId) return;
      const delta = event.clientX - startX;
      if (!dragged && Math.abs(delta) > 5) {
        dragged = true;
        rail.classList.remove('is-pressing');
        rail.classList.add('is-dragging');
        rail.setPointerCapture(event.pointerId);
      }
      if (!dragged) return;
      event.preventDefault();
      rail.scrollLeft = startScroll - delta;
    });
    const stop = (event) => {
      if (activePointerId !== event.pointerId) return;
      if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      rail.classList.remove('is-pressing');
      rail.classList.remove('is-dragging');
      suppressClick = dragged;
      if (dragged) suppressClickUntil = performance.now() + 250;
      activePointerId = null;
      dragged = false;
      window.setTimeout(() => { suppressClick = false; }, 250);
    };
    rail.addEventListener('pointerup', stop);
    rail.addEventListener('pointercancel', stop);
    rail.addEventListener('pointerleave', (event) => {
      if (!dragged) stop(event);
    });
    rail.addEventListener('dragstart', (event) => event.preventDefault());
    rail.addEventListener('click', (event) => {
      if (suppressClick || performance.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.defaultPrevented || event.target.closest('a, button, input, select, textarea, label')) return;
      const productSlide = event.target.closest('[data-airacker-product-slide]');
      if (productSlide?.dataset.productUrl) window.location.assign(productSlide.dataset.productUrl);
    }, true);
  });
};

const bindAirackerLoopRails = (scope = document) => {
  scope.querySelectorAll('[data-airacker-loop-rail]').forEach((rail) => {
    if (rail.dataset.airackerLoopBound === 'true') return;
    rail.dataset.airackerLoopBound = 'true';
    const realCards = [...rail.children];
    if (realCards.length < 2) return;
    const makeClone = (card, position) => {
      const clone = card.cloneNode(true);
      clone.dataset.airackerLoopClone = position;
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea').forEach((node) => {
        node.tabIndex = -1;
      });
      return clone;
    };

    let frame;
    let loopEnabled = false;
    let lastWidth = -1;
    const refresh = () => {
      const width = rail.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      rail.querySelectorAll(':scope > [data-airacker-loop-clone]').forEach((card) => card.remove());
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
      const step = realCards[0].offsetWidth + gap;
      loopEnabled = width > 0 && step > 0 && realCards.length * step - gap > width + 1;
      if (!loopEnabled) {
        rail.scrollLeft = 0;
        return;
      }
      const cloneCount = Math.min(realCards.length, Math.ceil(width / step) + 1);
      realCards.slice(0, cloneCount).forEach((card) => rail.append(makeClone(card, 'after')));
      realCards.slice(-cloneCount).reverse().forEach((card) => rail.prepend(makeClone(card, 'before')));
      rail.scrollLeft = realCards[0].offsetLeft;
    };
    const normalize = () => {
      if (!loopEnabled) return;
      const step = realCards[1].offsetLeft - realCards[0].offsetLeft;
      const cycleSpan = step * realCards.length;
      const startBoundary = realCards[0].offsetLeft - step / 2;
      const endBoundary = startBoundary + cycleSpan;
      if (cycleSpan <= 0) return;
      const current = rail.scrollLeft;
      if (current >= startBoundary && current < endBoundary) return;
      const offset = ((current - startBoundary) % cycleSpan + cycleSpan) % cycleSpan;
      const target = Math.min(rail.scrollWidth - rail.clientWidth, Math.max(0, startBoundary + offset));
      if (Math.abs(target - current) > 1) rail.scrollLeft = target;
    };
    rail.addEventListener('scroll', () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(normalize);
    }, { passive: true });
    window.requestAnimationFrame(refresh);
    new ResizeObserver(refresh).observe(rail);
  });
};

const openAirackerGalleryDialog = (opener, dialogId) => {
  const dialog = document.getElementById(dialogId);
  if (!dialog || typeof dialog.showModal !== 'function') return;
  dialog.airackerLastOpener = opener;
  if (!dialog.open) dialog.showModal();
  dialog.querySelector('[data-airacker-gallery-close]')?.focus();
};

const bindAirackerGalleryDialogs = (scope = document) => {
  scope.querySelectorAll('[data-airacker-gallery-open]').forEach((opener) => {
    if (opener.dataset.airackerGalleryOpenBound === 'true') return;
    opener.dataset.airackerGalleryOpenBound = 'true';
    opener.addEventListener('click', () => {
      openAirackerGalleryDialog(opener, opener.dataset.airackerGalleryOpen);
    });
  });

  scope.querySelectorAll('[data-airacker-gallery-dialog]').forEach((dialog) => {
    if (dialog.dataset.airackerDialogBound === 'true') return;
    dialog.dataset.airackerDialogBound = 'true';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      if (dialog.airackerSkipFocusReturn) {
        dialog.airackerSkipFocusReturn = false;
        return;
      }
      if (dialog.airackerLastOpener?.isConnected) dialog.airackerLastOpener.focus();
    });
  });
};

const bindAirackerStoryCarousels = (scope = document) => {
  scope.querySelectorAll('[data-airacker-story-carousel]').forEach((carousel) => {
    if (carousel.dataset.airackerStoryBound === 'true') return;
    carousel.dataset.airackerStoryBound = 'true';
    const slides = [...carousel.querySelectorAll('[data-airacker-story-slide]')];
    if (!slides.length) return;
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;
    let userPaused = false;
    let activePointerId;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerDeltaX = 0;
    let isDragging = false;
    let suppressClick = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const interval = Math.max(3, Number.parseInt(carousel.dataset.autoplaySeconds, 10) || 3) * 1000;
    const controls = carousel.querySelector('[data-airacker-story-controls]');
    const dots = [...carousel.querySelectorAll('[data-airacker-story-dot]')];
    const toggle = carousel.querySelector('[data-airacker-story-toggle]');
    const pauseIcon = toggle?.querySelector('[data-airacker-story-pause]');
    const playIcon = toggle?.querySelector('[data-airacker-story-play]');

    const show = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        if (slide.matches('a')) slide.tabIndex = isActive ? 0 : -1;
      });
      dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === activeIndex)));
    };
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (slides.length > 1 && !reducedMotion.matches && !userPaused) timer = window.setInterval(() => show(activeIndex + 1), interval);
    };
    const restart = () => {
      stop();
      start();
    };
    const syncToggle = () => {
      if (!toggle) return;
      const isPaused = userPaused || reducedMotion.matches;
      toggle.setAttribute('aria-label', isPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
      if (pauseIcon) pauseIcon.hidden = isPaused;
      if (playIcon) playIcon.hidden = !isPaused;
    };
    const finishDrag = (event, cancelled = false) => {
      if (event.pointerId !== activePointerId) return;
      const wasDragging = isDragging;
      if (carousel.hasPointerCapture?.(event.pointerId)) carousel.releasePointerCapture(event.pointerId);
      if (!cancelled && wasDragging && Math.abs(pointerDeltaX) >= 48) {
        show(activeIndex + (pointerDeltaX < 0 ? 1 : -1));
      }
      suppressClick = wasDragging;
      activePointerId = undefined;
      pointerDeltaX = 0;
      isDragging = false;
      carousel.classList.remove('is-dragging');
      window.setTimeout(() => {
        suppressClick = false;
      }, 0);
      restart();
    };

    carousel.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0 || event.target.closest('[data-airacker-story-controls]')) return;
      activePointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      pointerDeltaX = 0;
      isDragging = false;
      stop();
    });
    carousel.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointerId) return;
      pointerDeltaX = event.clientX - pointerStartX;
      const pointerDeltaY = event.clientY - pointerStartY;
      if (!isDragging && Math.abs(pointerDeltaX) >= 12 && Math.abs(pointerDeltaX) > Math.abs(pointerDeltaY)) {
        isDragging = true;
        carousel.setPointerCapture?.(event.pointerId);
        carousel.classList.add('is-dragging');
      }
      if (isDragging) event.preventDefault();
    });
    carousel.addEventListener('pointerup', (event) => finishDrag(event));
    carousel.addEventListener('pointercancel', (event) => finishDrag(event, true));
    carousel.addEventListener('dragstart', (event) => event.preventDefault());
    carousel.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', (event) => {
      if (!carousel.contains(event.relatedTarget)) start();
    });
    controls?.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-airacker-story-dot]');
      if (dot) {
        show(Number.parseInt(dot.dataset.airackerStoryDot, 10) || 0);
        restart();
      }
    });
    toggle?.addEventListener('click', () => {
      if (reducedMotion.matches) return;
      userPaused = !userPaused;
      syncToggle();
      if (userPaused) stop();
      else start();
    });
    reducedMotion.addEventListener?.('change', () => {
      syncToggle();
      if (reducedMotion.matches) stop();
      else start();
    });
    show(activeIndex);
    syncToggle();
    start();
  });
};

const bindAirackerVideoRails = (scope = document) => {
  scope.querySelectorAll('[data-airacker-video-rail]').forEach((rail) => {
    if (rail.dataset.airackerVideoBound === 'true') return;
    rail.dataset.airackerVideoBound = 'true';
    const realCards = [...rail.querySelectorAll('[data-airacker-video-card]')];
    if (!realCards.length) return;
    const cloneCount = Math.min(realCards.length, 2);
    const createClone = (card, position) => {
      const clone = card.cloneNode(true);
      clone.dataset.airackerVideoClone = position;
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('role', 'presentation');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea').forEach((node) => {
        node.tabIndex = -1;
      });
      clone.querySelectorAll('video').forEach((video) => {
        video.preload = 'none';
        video.removeAttribute('src');
        video.removeAttribute('data-airacker-video-src');
        video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      });
      return clone;
    };

    realCards.forEach((card, index) => {
      card.dataset.airackerVideoRealIndex = String(index);
    });
    realCards.slice(0, cloneCount).forEach((card) => {
      rail.append(createClone(card, 'after'));
    });
    realCards.slice(-cloneCount).reverse().forEach((card) => {
      rail.prepend(createClone(card, 'before'));
    });

    const cards = [...rail.querySelectorAll('[data-airacker-video-card]')];
    let activeCard;
    let frame;
    let ready = false;
    let dragged = false;

    const hydrateVideo = (video) => {
      if (!video || video.dataset.airackerVideoLoaded === 'true') return;
      const card = video.closest('[data-airacker-video-card]');
      const deferredSource = video.dataset.airackerVideoSrc;
      if (deferredSource) video.src = deferredSource;
      video.preload = 'auto';
      video.dataset.airackerVideoLoaded = 'true';
      card?.classList.add('is-video-loading');
      video.addEventListener('loadeddata', () => {
        card?.classList.remove('is-video-loading');
        card?.classList.add('is-video-ready');
      }, { once: true });
      video.addEventListener('error', () => {
        card?.classList.remove('is-video-loading');
      }, { once: true });
      video.load();
    };
    const centeredScrollLeft = (card) => card.offsetLeft - (rail.clientWidth - card.offsetWidth) / 2;
    const centerCard = (card, behavior = 'smooth') => {
      rail.scrollTo({ left: centeredScrollLeft(card), behavior });
    };
    const update = () => {
      if (!ready) return;
      const center = rail.getBoundingClientRect().left + rail.clientWidth / 2;
      let nearestCard = cards[0];
      let distance = Infinity;
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const candidate = Math.abs(rect.left + rect.width / 2 - center);
        if (candidate < distance) {
          distance = candidate;
          nearestCard = card;
        }
      });
      if (nearestCard.dataset.airackerVideoClone) {
        const realIndex = Number.parseInt(nearestCard.dataset.airackerVideoRealIndex, 10);
        const matchingRealCard = realCards[realIndex];
        if (matchingRealCard) {
          rail.scrollTo({
            left: rail.scrollLeft + matchingRealCard.offsetLeft - nearestCard.offsetLeft,
            behavior: 'auto',
          });
          nearestCard = matchingRealCard;
        }
      }
      if (nearestCard !== activeCard) {
        activeCard = nearestCard;
      }
      cards.forEach((card) => {
        const video = card.querySelector('video');
        const isActive = card === activeCard;
        card.classList.toggle('is-active', isActive);
        if (!card.dataset.airackerVideoClone) card.setAttribute('aria-current', String(isActive));
        if (!video) return;
        const shouldPrepare = isActive && rail.dataset.nearby === 'true';
        const shouldPlay = isActive && rail.dataset.visible === 'true';
        if (shouldPrepare) hydrateVideo(video);
        if (shouldPlay) {
          hydrateVideo(video);
          video.muted = true;
          if (video.paused && video.dataset.airackerPlayPending !== 'true') {
            video.dataset.airackerPlayPending = 'true';
            video.play().catch(() => {}).finally(() => {
              delete video.dataset.airackerPlayPending;
            });
          }
        } else {
          video.pause();
        }
      });
    };
    const requestUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    rail.addEventListener('click', (event) => {
      const card = event.target.closest('[data-airacker-video-card]');
      if (!card || card === activeCard || event.target.closest('a, button')) return;
      if (dragged) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      centerCard(card);
    });

    rail.addEventListener('keydown', (event) => {
      const card = event.target.closest('[data-airacker-video-card]');
      if (!card || card === activeCard || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      centerCard(card);
    });

    realCards.forEach((card) => {
      card.tabIndex = 0;
    });

    let dragStartX = 0;
    let dragStartScroll = 0;
    rail.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('a, button')) return;
      dragStartX = event.clientX;
      dragStartScroll = rail.scrollLeft;
      dragged = false;
      rail.classList.add('is-dragging');
      rail.setPointerCapture(event.pointerId);
    });
    rail.addEventListener('pointermove', (event) => {
      if (!rail.hasPointerCapture(event.pointerId)) return;
      const delta = event.clientX - dragStartX;
      if (Math.abs(delta) > 4) dragged = true;
      rail.scrollLeft = dragStartScroll - delta;
    });
    const stopDragging = (event) => {
      if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      rail.classList.remove('is-dragging');
      if (dragged) requestUpdate();
      window.setTimeout(() => { dragged = false; }, 0);
    };
    rail.addEventListener('pointerup', stopDragging);
    rail.addEventListener('pointercancel', stopDragging);
    rail.addEventListener('dragstart', (event) => event.preventDefault());
    rail.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    const loadObserver = new IntersectionObserver(([entry]) => {
      rail.dataset.nearby = String(entry.isIntersecting);
      requestUpdate();
    }, { rootMargin: '500px 0px', threshold: 0 });
    loadObserver.observe(rail);
    const playObserver = new IntersectionObserver(([entry]) => {
      rail.dataset.visible = String(entry.isIntersecting);
      update();
    }, { threshold: 0.35 });
    playObserver.observe(rail);
    window.requestAnimationFrame(() => {
      ready = true;
      centerCard(realCards[0], 'auto');
      update();
    });
  });
};

const bindAirackerBuyNow = (scope = document) => {
  scope.querySelectorAll('[data-airacker-buy-now]').forEach((button) => {
    if (button.dataset.airackerBuyNowBound === 'true') return;
    button.dataset.airackerBuyNowBound = 'true';
    button.addEventListener('click', async () => {
      const form = button.closest('form');
      const errorWrapper = button.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
      if (!form || button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (errorWrapper) errorWrapper.hidden = true;
      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added to your cart.');
        window.location.assign('/checkout');
      } catch (error) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (errorWrapper && errorMessage) {
          errorMessage.textContent = error.message;
          errorWrapper.hidden = false;
        }
      }
    });
  });
};

class AirackerCartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.airackerRecommendationsBound === 'true') return;
    this.dataset.airackerRecommendationsBound = 'true';
    this.load();
  }

  async load() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const grid = this.querySelector('[data-airacker-cart-recommendations-grid]');
    if (!productId || !sectionId || !grid) {
      this.hidden = true;
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=6&intent=related`;

    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Recommendation request failed with ${response.status}.`);
      const html = await response.text();
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const recommendationGrid = fragment.querySelector('[data-airacker-cart-recommendations-grid]');
      if (!recommendationGrid?.children.length) {
        this.hidden = true;
        return;
      }
      grid.replaceChildren(...[...recommendationGrid.children].map((child) => child.cloneNode(true)));
    } catch (error) {
      this.hidden = true;
      console.warn('AIRACKER cart recommendations could not be loaded.', error);
    }
  }
}

// Reset the cache after the 0908 catalogue image replacement so returning
// visitors never see stale CDN URLs in the cart drawer.
const airackerRecentProductsKey = 'airacker:recent-products:v2';

const readAirackerRecentProducts = () => {
  try {
    return JSON.parse(localStorage.getItem(airackerRecentProductsKey)) || [];
  } catch {
    return [];
  }
};

const trackAirackerProductView = () => {
  const source = document.querySelector('[data-airacker-product-view]');
  if (!source?.dataset.productId) return;
  const product = {
    id: source.dataset.productId,
    title: source.dataset.productTitle || '',
    url: source.dataset.productUrl || '',
    image: source.dataset.productImage || '',
    price: source.dataset.productPrice || '',
  };
  const products = [product, ...readAirackerRecentProducts().filter((item) => item.id !== product.id)].slice(0, 8);
  try {
    localStorage.setItem(airackerRecentProductsKey, JSON.stringify(products));
  } catch (error) {
    console.warn('AIRACKER recently viewed products could not be saved.', error);
  }
};

class AirackerRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  escape(value = '') {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }

  render() {
    const products = readAirackerRecentProducts().slice(0, 3);
    if (!products.length) {
      this.innerHTML = '';
      return;
    }
    this.innerHTML = `<div class="airacker-cart-recent__grid">${products.map((product) => `
      <a class="airacker-cart-recent__item" href="${this.escape(product.url)}">
        ${product.image ? `<img src="${this.escape(product.image)}" alt="" width="160" height="160" loading="lazy">` : ''}
        <span>${this.escape(product.title)}</span>
        <small>${this.escape(product.price)}</small>
      </a>`).join('')}</div>`;
  }
}

document.addEventListener('shopify:section:load', (event) => {
  bindAirackerMegaMenus(event.target);
  bindAirackerRailControls(event.target);
  bindAirackerDraggableRails(event.target);
  bindAirackerLoopRails(event.target);
  bindAirackerGalleryDialogs(event.target);
  bindAirackerStoryCarousels(event.target);
  bindAirackerVideoRails(event.target);
  bindAirackerBuyNow(event.target);
});

document.addEventListener('DOMContentLoaded', () => {
  trackAirackerProductView();
  if (!customElements.get('airacker-cart-recommendations')) {
    customElements.define('airacker-cart-recommendations', AirackerCartRecommendations);
  }
  if (!customElements.get('airacker-recently-viewed')) {
    customElements.define('airacker-recently-viewed', AirackerRecentlyViewed);
  }
  bindAirackerMegaMenus();
  new AirackerWishlist();
  new AirackerQuickAdd();
  document.querySelectorAll('[data-airacker-popup]').forEach((node) => new AirackerPopup(node));
  bindAirackerRailControls();
  bindAirackerDraggableRails();
  bindAirackerLoopRails();
  bindAirackerGalleryDialogs();
  bindAirackerStoryCarousels();
  bindAirackerVideoRails();
  bindAirackerBuyNow();
  document.addEventListener('click', (event) => {
    const passwordToggle = event.target.closest('[data-airacker-password-toggle]');
    const galleryCloser = event.target.closest('[data-airacker-gallery-close]');
    const gallerySwitcher = event.target.closest('[data-airacker-gallery-switch]');
    if (passwordToggle) {
      const passwordInput = document.getElementById(passwordToggle.getAttribute('aria-controls'));
      if (passwordInput) {
        const isVisible = passwordInput.type === 'text';
        passwordInput.type = isVisible ? 'password' : 'text';
        passwordToggle.setAttribute('aria-pressed', String(!isVisible));
        passwordToggle.setAttribute(
          'aria-label',
          isVisible ? passwordToggle.dataset.showLabel : passwordToggle.dataset.hideLabel
        );
      }
    }
    if (galleryCloser) galleryCloser.closest('dialog')?.close();
    if (gallerySwitcher) {
      const currentDialog = gallerySwitcher.closest('dialog');
      const returnFocus = currentDialog?.airackerLastOpener || gallerySwitcher;
      if (currentDialog) currentDialog.airackerSkipFocusReturn = true;
      currentDialog?.close();
      openAirackerGalleryDialog(returnFocus, gallerySwitcher.dataset.airackerGallerySwitch);
    }
  });

  const recentKey = 'airacker:recent-searches:v1';
  const readRecent = () => {
    try { return JSON.parse(localStorage.getItem(recentKey)) || []; } catch { return []; }
  };
  const escapeText = (value = '') => {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  };
  document.querySelectorAll('.search-modal form[role="search"]').forEach((form) => {
    form.addEventListener('submit', () => {
      const value = window.AirackerSearch.normalize(form.querySelector('input[type="search"]')?.value || '');
      if (!value) return;
      const terms = [value, ...readRecent().filter((term) => term.toLowerCase() !== value.toLowerCase())].slice(0, 5);
      localStorage.setItem(recentKey, JSON.stringify(terms));
    });
  });
  document.querySelectorAll('[data-airacker-recent-searches]').forEach((container) => {
    const terms = readRecent();
    const heading = `<h3>${escapeText(container.dataset.heading || 'Search History')}</h3>`;
    if (!terms.length) {
      container.innerHTML = `${heading}<p class="airacker-search-history__empty">${escapeText(container.dataset.empty || 'No recent searches')}</p>`;
      return;
    }
    container.innerHTML = `${heading}<ul class="list-unstyled" role="list">${terms.map((term) => `<li><a href="${window.AirackerSearch.buildUrl(term)}">${escapeText(term)}</a></li>`).join('')}</ul>`;
  });
});
